<script setup lang="ts">
import { listEntities } from '@/backend/entity'
import { getProject } from '@/backend/getProject'
import type { FieldEmits } from '@/modules/Project/Fields/field-types'
import type { Field } from '@/modules/Project/Fields/types'
import { TYPE_ICON } from '@/modules/Project/icons'
import type { CollectionProperty, Property } from '@/modules/Project/Properties/types'
import { serializeField, serializeProperty } from '@/modules/Project/serializer'
import { useProjectStore } from '@/modules/Project/useProjectStore'
import { useResolveProjectRoute } from '@/modules/Project/useResolveProjectRoute'
import { useSerializeFieldToText } from '@/modules/Project/useSerializeFieldToText'
import { getFirstNLines, isValidUrl } from '@/shared/utils/string'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  field: Field<'collection'>
  property: CollectionProperty
}>()

defineEmits<FieldEmits<'collection'>>()

const MAX_ENTITY_NUMBER = 10 // Reduced from 50 to fit better in sidebar

const serializeFieldToText = useSerializeFieldToText()

const { activeView } = useProjectStore()

const resolveProjectRoute = useResolveProjectRoute()

const properties = ref<Property[]>([])
const data = ref<Array<{ id: string; data: Record<string, string> }>>([])
const isLoading = ref(false)
const error = ref<string | null>(null)
const extraEntitiesCount = ref(0)

const projectRoute = computed(() =>
  resolveProjectRoute({
    projectId: props.property.config.subprojectConfig.child_project_id,
    parentProjectId: props.field.projectId,
    parentPropertyId: props.property.id,
    parentEntityId: props.field.entityId,
    parentViewId: activeView?.id,
    workspaceId: props.field.workspaceId,
  }),
)

// Watch for property changes to load child agent properties
watch(
  () => props.property,
  async (property) => {
    isLoading.value = true
    error.value = null
    try {
      const projectRes = await getProject(
        property.workspaceId,
        property.config.subprojectConfig.child_project_id,
      )
      if (!projectRes.ok) {
        error.value = 'Failed to load agent properties'
        return
      }

      properties.value = projectRes.data.properties.map((p) =>
        serializeProperty(p, projectRes.data.workspace_id, projectRes.data.id),
      )
    } catch (e) {
      error.value = 'Error loading project'
      throw e
    } finally {
      isLoading.value = false
    }
  },
  { immediate: true },
)

// Watch for entity changes and property list changes to reload collection data
watch(
  () => [props.field.entityId, properties.value] as const,
  async ([newParentEntityId, newProperties]) => {
    if (!newParentEntityId || newProperties.length === 0) {
      return
    }

    isLoading.value = true
    error.value = null
    try {
      const entitiesResult = await listEntities({
        workspaceId: props.field.workspaceId,
        projectId: props.property.config.subprojectConfig.child_project_id,
        offset: 0,
        limit: MAX_ENTITY_NUMBER,
        parent_entity_id: newParentEntityId,
      })
      if (!entitiesResult.ok) {
        error.value = 'Failed to load collection items'
        return
      }

      const { total_count, page_size } = entitiesResult.data.metadata

      if (typeof total_count === 'number') {
        extraEntitiesCount.value = total_count - page_size
      }

      data.value = entitiesResult.data.data.map((d) => ({
        id: d.id,
        data: Object.fromEntries(
          Object.values(d.fields).map((f) => {
            const field = serializeField(f, props.field.workspaceId, d.project_id)
            const fieldProperty = newProperties.find((p) => p.id === f.property_id)
            return [f.property_id, serializeFieldToText(field, fieldProperty)]
          }),
        ),
      }))
    } catch (e) {
      error.value = 'Error loading collection items'
      throw e
    } finally {
      isLoading.value = false
    }
  },
  { immediate: true },
)
</script>

<template>
  <div
    v-if="isLoading"
    class="flex h-7 items-center justify-center text-sm-12px-light text-text-subtle"
  >
    Loading...
  </div>
  <div
    v-else-if="error"
    class="flex h-7 items-center justify-center text-sm-12px-light text-text-critical"
  >
    {{ error }}
  </div>
  <div
    v-else-if="data.length === 0 || properties.length === 0"
    class="flex h-7 items-center justify-center text-sm-12px-light text-text-subtle"
  >
    No items in collection
  </div>
  <div
    v-else
    class="flex w-full flex-col"
  >
    <div
      class="go-scrollbar my-1 max-h-[400px] min-h-0 max-w-full min-w-full shrink grow-0 overflow-auto rounded-corner-4 border border-border-subtle bg-surface-primary"
    >
      <table class="max-w-full min-w-full rounded-corner-4">
        <thead
          class="cursor-pointer"
          aria-role="rowgroup"
          aria-roledescription="Table header"
        >
          <tr class="hover:bg-background-transparent-hovered">
            <th
              v-for="prop in properties"
              :key="prop.id"
              class="border-r border-b border-border-subtle px-2 py-px last:border-r-0"
            >
              <div class="flex max-w-60 flex-row items-center gap-1.5 px-1 py-1.5">
                <IconSprite
                  size="md"
                  :icon="TYPE_ICON[prop.type]"
                  class="fill-icon-subtlest text-icon-subtlest"
                />
                <span class="truncate text-sm-12px-default text-text-subtle">{{ prop.name }}</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody
          aria-role="rowgroup"
          aria-roledescription="Table body"
        >
          <tr
            v-for="(item, rowIndex) in data"
            :key="item.id"
            aria-role="row"
            class="h-8 overflow-hidden border-b border-border-subtle first:border-t last:border-b-0 hover:bg-background-transparent-hovered"
          >
            <td
              v-for="prop in properties"
              :key="`${item.id}-${prop.id}`"
              class="border-r border-border-subtle last:border-r-0"
              :class="[rowIndex >= data.length - 1 ? '' : 'border-b']"
            >
              <div class="flex flex-row items-center px-3 py-2">
                <a
                  v-if="isValidUrl(item.data[prop.id] ?? '')"
                  class="truncate text-sm-12px-light text-text underline"
                  :href="item.data[prop.id]"
                  target="_blank"
                  >{{ item.data[prop.id] }}</a
                >
                <span
                  v-else
                  class="truncate text-sm-12px-light text-text"
                  >{{ getFirstNLines(item.data[prop.id] || '', 2) }}</span
                >
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="flex min-h-7 min-w-0 flex-row items-center pt-1 pb-1">
      <span
        v-if="extraEntitiesCount > 0"
        class="pr-2 text-sm-12px-light whitespace-nowrap text-text-subtlest"
        >{{ extraEntitiesCount }} more entities not shown</span
      >
      <DarwinButton
        :to="projectRoute"
        size="xs"
        variant="transparent-link"
      >
        <template #leading-icon>
          <IconSprite icon="table" />
        </template>
        Open in table view
        <template #trailing-icon>
          <IconSprite icon="chevron-right" />
        </template>
      </DarwinButton>
    </div>
  </div>
</template>
